import { useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Card, Checkbox, Chip, Modal, ModalDialog, Stack, Typography } from '@mui/joy';
import { ArrowLeft, Images, PencilSimple } from '@phosphor-icons/react';
import { PhotoAnnotationEditor } from '../../components/photos/PhotoAnnotationEditor';
import { EvidenceImage } from './EvidenceImage';
import { proposalDealsApi, dealError, type Deal } from '../../lib/proposalDealsApi';
import type { Photo } from '../../types';

export function DealOpportunityPhotos({ deal, finding, item, refresh, onClose }: {
  deal: Deal; finding?: number; item?: number; refresh: () => Promise<void>; onClose: () => void;
}) {
  const [selected, setSelected] = useState<number[]>([]);
  const [editing, setEditing] = useState<Photo | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const photos = useInfiniteQuery({
    queryKey: ['proposal-opportunity-photos', deal.id],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => proposalDealsApi.opportunityPhotos(deal.id, pageParam),
    getNextPageParam: (last, pages) => last.next ? pages.length + 1 : undefined,
  });
  const attached = new Set(deal.evidence.filter(photo => finding ? photo.finding === finding : photo.repair_item === item).map(photo => photo.source_photo));
  const title = finding ? deal.findings.find(value => value.id === finding)?.title : deal.repair_items.find(value => value.id === item)?.title;
  const addPhotos = async () => {
    if (submitting.current || !selected.length) return;
    submitting.current = true;
    setPending(true);
    setError('');
    try {
      await proposalDealsApi.action(deal.id, 'import-photos', { photo_ids: selected, ...(finding ? { finding } : { repair_item: item }) });
      await refresh();
      onClose();
    } catch (failure) {
      setError(dealError(failure));
    } finally {
      submitting.current = false;
      setPending(false);
    }
  };
  return <>
    <Modal open={!editing} onClose={() => { if (!pending) onClose(); }}>
      <ModalDialog aria-labelledby="opportunity-photos-title" sx={{ width: 'min(960px, calc(100vw - 24px))', maxHeight: 'calc(100dvh - 24px)', p: { xs: 2, md: 3 }, gap: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography id="opportunity-photos-title" level="h4" startDecorator={<Images />}>Opportunity photos</Typography>
          <Button variant="plain" disabled={pending} onClick={onClose} startDecorator={<ArrowLeft />}>Back</Button>
        </Stack>
        <Box>
          <Typography level="title-sm">Add to {title}</Typography>
          <Typography level="body-sm">Review and annotate, then select up to 30 photos. Annotations save to the Opportunity; added evidence keeps its own copy.</Typography>
        </Box>
        {error && <Alert color="danger">{error}</Alert>}
        <Box sx={{ overflowY: 'auto', minHeight: 100, flex: 1 }}>
          {photos.isPending && <Typography role="status">Loading Opportunity photos…</Typography>}
          {photos.isError && <Alert color="danger" endDecorator={<Button variant="plain" onClick={() => void photos.refetch()}>Retry</Button>}>Could not load photos. Your selection is retained.</Alert>}
          {!photos.isPending && !photos.isError && photos.data?.pages[0].count === 0 && <Alert color="neutral">No photos in this Opportunity yet. Upload or capture photos to get started.</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            {photos.data?.pages.flatMap(page => page.results).map(photo => {
              const added = attached.has(photo.id);
              const ready = !!photo.image_url && (!photo.upload_status || ['completed', 'legacy'].includes(photo.upload_status)) && (!photo.is_heic || photo.conversion_status === 'completed');
              const checked = selected.includes(photo.id);
              return <Card key={photo.id} variant="outlined" sx={{ p: 1, gap: 1, borderColor: checked ? 'primary.500' : undefined, bgcolor: checked ? 'primary.softBg' : undefined }}>
                <EvidenceImage photo={{ image_url: photo.image_url, caption: photo.description || '', annotations: photo.annotations }} />
                <Checkbox label={photo.description || `Photo ${photo.id}`} checked={checked || added} disabled={pending || added || !ready || (!checked && selected.length >= 30)} onChange={() => setSelected(ids => checked ? ids.filter(id => id !== photo.id) : [...ids, photo.id])} slotProps={{ label: { sx: { overflowWrap: 'anywhere', fontSize: 13 } } }} />
                {added ? <Chip color="success" size="sm">Already added</Chip> : !ready ? <Chip size="sm">Photo not ready</Chip> : <Button variant="plain" size="sm" startDecorator={<PencilSimple />} disabled={pending} onClick={() => setEditing(photo)} aria-label={`Review & annotate ${photo.description || `photo ${photo.id}`}`}>Review & annotate</Button>}
              </Card>;
            })}
          </Box>
          {photos.hasNextPage && <Button sx={{ mt: 2 }} fullWidth variant="soft" loading={photos.isFetchingNextPage} disabled={pending} onClick={() => void photos.fetchNextPage()}>Load more photos</Button>}
        </Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography level="body-sm" role="status">{selected.length} selected</Typography>
          <Button loading={pending} disabled={!selected.length || deal.scope_locked} onClick={() => void addPhotos()}>Add {selected.length || ''} {selected.length === 1 ? 'photo' : 'photos'}</Button>
        </Stack>
      </ModalDialog>
    </Modal>
    {editing && <PhotoAnnotationEditor photo={editing} onClose={() => setEditing(null)} onSaved={() => { void photos.refetch(); }} />}
  </>;
}
